import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ReportsService } from '../../../services/reports/reports.service';
import { Snackbar } from '../../../services/snackbar/snackbar.service';
import { getUniqueId } from '../../../functions/get-unique-id';
import { ValidatorsEx } from '../../../validators/validators-ex';
import { ReportField } from '../../../models/api/reports/report';
import { SelectComponent } from '../../core/controls/select.component';
import { InputComponent } from '../../core/controls/input.component';
import { RangeComponent } from '../../core/controls/range.component';
import { ButtonComponent } from '../../core/buttons/button.component';
import { ReportFieldDialogComponent } from './report-field-dialog.component';
import {
  ReorderableFieldListComponent,
  ReorderableFormChangedEvent,
  ReorderableFormRowDescriptor,
} from './reorderable-field-list.component';
import { PageHeaderComponent } from '../../core/labels/page-header.component';
import { ActionRowComponent } from '../../core/panels/action-row.component';

@Component({
  selector: 'app-dyo-financial-fields',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SelectComponent,
    InputComponent,
    RangeComponent,
    ButtonComponent,
    ReportFieldDialogComponent,
    ReorderableFieldListComponent,
    PageHeaderComponent,
    ActionRowComponent,
  ],
  template: `
    <app-page-header label="Financial Fields" headerSize="h3" />

    <div>
      <app-action-row>
        <app-button
          left1
          emphasis="Low"
          label="Add financial field"
          [disabled]="!canAddMore()"
          (click)="addField()"
        />
      </app-action-row>
      <div class="tw-pb-6"></div>

      <app-reorderable-field-list
        layout="row"
        focusHost="app-dyo-financial-fields"
        [formArray]="fg.controls.fields"
        [mapRow]="mapFinancialRow"
        [showClearOnTile]="true"
        removeAriaLabel="Remove financial field"
        [rowExtras]="extras"
        (edit)="onEdit($event.index)"
        (clear)="clearField($event.index)"
        (changed)="onListChanged($event)"
      />

      <ng-template #extras let-fg let-item="item">
        @if (item.hasField) {
          <div class="tw-flex tw-flex-col">
            <div class="tw-text-sm tw-pb-1 tw-pl-2">
              Limit results to changes...
            </div>
            <div class="tw-flex tw-items-center tw-gap-2">
              <div class="tw-w-[200px] tw-flex-shrink-0">
                <app-select
                  [control]="fg.controls.operator"
                  [options]="operatorOptions"
                  optionLabel="name"
                  optionValue="code"
                  [filter]="false"
                  [showClear]="false"
                  [virtualScroll]="false"
                  label="Operator"
                  [showHelpText]="false"
                />
              </div>

              @if (showSingleAmount(fg.controls.operator.value)) {
                <div class="tw-w-[200px] tw-flex-shrink-0 tw-relative">
                  <app-input
                    [control]="fg.controls.amountFrom"
                    type="Currency"
                    label="Amount"
                    rightIcon="fa-light fa-dollar-sign"
                    screenReaderLabel="Amount"
                  />
                </div>
              }

              @if (showRange(fg.controls.operator.value)) {
                <div class="tw-w-[400px] tw-flex-shrink-0">
                  <app-range
                    [control]="fg.controls.amountFrom"
                    [toControl]="fg.controls.amountTo"
                    type="Currency"
                  />
                </div>
              }
            </div>
          </div>
        }
      </ng-template>

      <div class="tw-italic">
        Select up to {{ maxFields() }} financial fields.
      </div>
    </div>

    <app-report-field-dialog
      #fieldDialog
      (selected)="onFieldSelected($event)"
    />
  `,
  styles: `
    :host ::ng-deep .tw-relative app-help-text {
      position: absolute;
      left: 0;
      right: 0;
    }
    :host ::ng-deep app-range fieldset legend {
      display: none;
    }
    :host ::ng-deep app-range app-input {
      min-width: 0;
      position: relative;
    }
    :host ::ng-deep app-range app-help-text {
      position: absolute;
      left: 0;
      right: 0;
    }
  `,
})
export class DyoFinancialFieldsComponent implements OnInit {
  @ViewChild('fieldDialog') fieldDialog!: ReportFieldDialogComponent;

  private reports = inject(ReportsService);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private snackbar = inject(Snackbar);

  // Current control being edited
  private currentControl: FormControl | null = null;
  private currentIndex: number | null = null;

  // Form with array of financial field rows
  fg = new FormGroup({
    fields: new FormArray([]),
  });

  // Track count for computed signals
  fieldsCount = signal(0);

  readonly mapFinancialRow = (
    fieldFg: FormGroup,
    _i: number,
    _n: number,
  ): ReorderableFormRowDescriptor => {
    const label = this.getFieldLabel(fieldFg);
    const hasField = !!fieldFg.controls['field'].value;
    const slotLabel = fieldFg.controls['slotLabel'].value as string;
    return {
      id: fieldFg.controls['id'].value as string,
      label: hasField ? label || slotLabel : slotLabel,
      hasField,
      showRemove: true,
      removeAriaLabel: 'Remove financial field',
      fixedWidth: true,
      wrapLabel: true,
    };
  };

  ngOnInit(): void {
    this.loadSavedFields();
  }

  /** Reload fields from the current report. Called by parent on client change. */
  reload(): void {
    this.loadSavedFields();
  }

  private loadSavedFields(): void {
    // Clear existing form array before rebuilding
    this.fg.controls.fields.clear();

    const report = this.reports.currentReport();
    const slots = this.financialSlots();
    const savedFields = report?.reportRequest?.financialFields ?? [];

    for (const saved of savedFields) {
      if (saved.field) {
        const slot =
          slots.find((s) => s.columnId === saved.columnId) ??
          slots[this.fields.length];
        const formGroup = this.createFieldFormGroup(
          saved.field,
          saved.operator,
          saved.amountFrom,
          saved.amountTo,
          slot,
        );
        this.fg.controls.fields.push(formGroup);
      }
    }

    // Show one empty tile group by default
    if (this.fields.length === 0) {
      const formGroup = this.createFieldFormGroup(
        null,
        null,
        null,
        null,
        slots[0],
      );
      this.fg.controls.fields.push(formGroup);
    }

    this.fieldsCount.set(this.fields.length);
  }

  /** Returns the selected field's display name. */
  getFieldLabel(fg: FormGroup): string {
    const field = fg.controls['field']?.value as ReportField | null;
    return field?.defaultValue ?? '';
  }

  private financialSlots(): ReportField[] {
    return (this.reports.currentReport()?.reportRequest?.reportFields ?? []).filter(
      (f) => f.inlineFilterType === 'COMP_FINANCIAL',
    );
  }

  /** Next unused API slot — identity stays on the row when reordered. */
  private nextUnusedSlot(): ReportField | undefined {
    const used = new Set(
      this.fields.map((fg) => fg.controls['slotColumnId'].value as number | null),
    );
    return this.financialSlots().find((s) => !used.has(s.columnId));
  }

  private createFieldFormGroup(
    field: ReportField | null = null,
    operator: string | null = null,
    amountFrom: number | null = null,
    amountTo: number | null = null,
    slot?: ReportField,
  ): FormGroup {
    const assigned = slot ?? this.nextUnusedSlot();
    const slotIndex = assigned
      ? this.financialSlots().findIndex((s) => s.columnId === assigned.columnId)
      : this.fields.length;
    const slotLabel =
      assigned?.defaultValue ??
      `Financial ${String(slotIndex + 1).padStart(2, '0')}`;

    const formGroup = new FormGroup({
      id: new FormControl(getUniqueId('dyo-fin'), { nonNullable: true }),
      slotColumnId: new FormControl<number | null>(assigned?.columnId ?? null),
      slotLabel: new FormControl(slotLabel, { nonNullable: true }),
      field: new FormControl<ReportField | null>(field),
      operator: new FormControl<string | null>(operator),
      amountFrom: new FormControl<number | null>(amountFrom),
      amountTo: new FormControl<number | null>(amountTo),
    });

    this.updateAmountValidators(formGroup, operator);

    formGroup.controls['operator'].valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((op) => {
        this.updateAmountValidators(formGroup, op);
      });

    return formGroup;
  }

  private updateAmountValidators(
    formGroup: FormGroup,
    operator: string | null,
  ): void {
    const amountFromControl = formGroup.controls['amountFrom'];
    const amountToControl = formGroup.controls['amountTo'];

    amountFromControl.clearValidators();
    amountToControl.clearValidators();

    if (operator) {
      amountFromControl.addValidators(Validators.required);
      amountFromControl.markAsTouched();

      if (operator === 'BETWEEN' || operator === 'NOT_BETWEEN') {
        amountToControl.addValidators(Validators.required);
        amountFromControl.addValidators(
          ValidatorsEx.currencyLessThan(amountToControl),
        );
        amountToControl.addValidators(
          ValidatorsEx.currencyGreaterThan(amountFromControl),
        );
        amountToControl.markAsTouched();
      }
    }

    amountFromControl.updateValueAndValidity();
    amountToControl.updateValueAndValidity();
  }

  // Max fields from API (count of COMP_FINANCIAL fields in reportFields)
  maxFields = computed(() => this.financialSlots().length);

  canAddMore = computed(() => this.fieldsCount() < this.maxFields());

  // Operator options
  operatorOptions = [
    { code: 'BETWEEN', name: 'Between' },
    { code: 'EQUALS', name: 'Equal to' },
    { code: 'GREATERTHAN', name: 'Greater than' },
    { code: 'GREATERTHANEQUALTO', name: 'Greater than or equal to' },
    { code: 'LESSTHAN', name: 'Less than' },
    { code: 'LESSTHANEQUALTO', name: 'Less than or equal to' },
    { code: 'NOT_BETWEEN', name: 'Not between' },
    { code: 'NOTEQUALTO', name: 'Not equal to' },
  ];

  get fields(): FormGroup[] {
    return this.fg.controls.fields.controls as FormGroup[];
  }

  addField(): void {
    if (!this.canAddMore()) return;

    const formGroup = this.createFieldFormGroup();
    this.fg.controls.fields.push(formGroup);
    this.fieldsCount.set(this.fields.length);
    this.updateReport();
  }

  clearField(index: number): void {
    const fg = this.fields[index];
    if (fg) {
      fg.controls['field'].setValue(null);
      this.updateReport();
    }
  }

  onEdit(index: number): void {
    const fg = this.fields[index];
    if (!fg) return;
    this.showDialog(
      this.fieldDialog,
      fg.controls['field'] as FormControl,
      index,
    );
  }

  onListChanged(_e: ReorderableFormChangedEvent): void {
    this.fieldsCount.set(this.fields.length);
    this.cdr.markForCheck();
    this.updateReport();
  }

  showDialog(
    dialog: ReportFieldDialogComponent,
    control: FormControl,
    index: number,
  ): void {
    this.currentControl = control;
    this.currentIndex = index;

    const report = this.reports.currentReport();
    const reportId = report?.reportTemplate?.id ?? null;
    const ipColumn = this.fields[index]?.controls['slotColumnId'].value ?? null;

    dialog.show(reportId, ipColumn);
  }

  onFieldSelected(field: ReportField): void {
    if (!this.currentControl) return;

    const isDuplicate = this.fields.some(
      (fg, i) =>
        i !== this.currentIndex &&
        (fg.controls['field'].value as ReportField | null)?.columnName ===
          field.columnName,
    );

    if (isDuplicate) {
      this.snackbar.show('Error : Duplicate', 'Error');
      return;
    }

    this.currentControl.setValue(field);
    this.updateReport();
  }

  // Visibility logic for amount inputs
  showSingleAmount(operator: string | null): boolean {
    return (
      operator != null && operator !== 'BETWEEN' && operator !== 'NOT_BETWEEN'
    );
  }

  showRange(operator: string | null): boolean {
    return operator === 'BETWEEN' || operator === 'NOT_BETWEEN';
  }

  updateReport(): void {
    const financialFields = this.fields
      .map((fg) => {
        const field = fg.controls['field'].value as ReportField | null;
        const operator = fg.controls['operator'].value as string | null;
        const amountFrom = fg.controls['amountFrom'].value as number | null;
        const amountTo = fg.controls['amountTo'].value as number | null;
        const slotColumnId = fg.controls['slotColumnId'].value as number | null;

        if (!field) return null;

        return {
          columnId: slotColumnId ?? 0,
          tableAlias: field.tableAlias ?? null,
          columnName: field.columnName ?? null,
          operator,
          amountFrom,
          amountTo,
          field,
        };
      })
      .filter((f) => f !== null);

    this.reports.currentReport.update((r) => {
      if (r.reportRequest) {
        r.reportRequest.financialFields = financialFields;
      }
      return r;
    });
  }

  get invalid(): boolean {
    return this.fg.invalid;
  }
}
